<template>
  <v-text-field  data-testid="number__textfield" ref="element" :step="step" :style="`max-width: ${width} !important`" outlined dense type="number" :label="label" :clearable="clearable" :min="min || ''" :max="max || ''"
    :error-messages="computedErrorMessages" :prepend-inner-icon="prependiconinner" :append-icon="appendiconinner" v-model="displayContent" @click:clear="content = '';tempContent=''" @input="updateValue()" @blur="$emit('blur', $event)" :hide-details="computedHideDetails" :disabled="disabled"
    :placeholder="placeholder" :rules="rules"  :background-color="backgroundColor" class="number-field" v-bind="$attrs"
  >
    <template v-if="$slots.append" class="input-image-slot" v-slot:append>
      <slot name="append"></slot>
    </template>
  </v-text-field>
</template>

<script>
  export default {
    inheritAttrs: false,
    activated() {
      this.setValue();
      this.updateValue();
    },
    created() {
      this.setValue();
      this.updateValue();
    },
    data: () => ({
      content: "",
      tempContent: 0.00000000000001,
      minMaxError: ""
    }),
    props: {
      error: {
        type: [String, Array, Boolean],
      },
      appendiconinner: {
        type: String,
        default: ""
      },
      prependiconinner: {
        type: String,
        default: ""
      },
      label: {
        type: String,
        default: ""
      },
      step: {
        type: String,
        default: "1"
      },
      value: {
        type: [String, Number],
        // type: String,
        default: 0
      },
      min: {
        type: String,
        default: ""
      },
      backgroundColor: {
        type: String,
        default: ""
      },
      max: {
        type: [String, Number],
        default: ""
      },
      width: {
        type: String,
        default: ""
      },
      hidedetails: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      clearable: {
        type: Boolean,
        default: false
      },
      ignoredFieldDefaultValue:{
        type:Number,
        default:0.00000000000001
      },
      placeholder:{
        type:String,
        default:''
      },
      rules:{
        type:Array,
        default: () => [
            
          ]
      },
      showMinMaxError: {
        type: Boolean,
        default: true
      },
      allowEmpty: {
        type: Boolean,
        default: false
      }
    },
    methods: {
      allowsNegativeInput() {
        if (this.allowEmpty) return true;
        if (this.min === '' || this.min === null || this.min === undefined) return false;
        const minNum = Number(this.min);
        return !isNaN(minNum) && minNum < 0;
      },
      normalizeTypedInput(value) {
        if (value === null || value === undefined) return value;
        const str = String(value).trim();
        if (!str || str === '-') return str;
        const zeroMinusMatch = str.match(/^0-(\d+(?:\.\d+)?)$/);
        if (zeroMinusMatch) {
          return `-${zeroMinusMatch[1]}`;
        }
        return str;
      },
      isPartialNumericInput(value) {
        const str = String(value ?? '').trim();
        // Only unfinished forms: lone minus, minus-dot (no digits yet), or trailing decimal after digits.
        // Do not treat complete values like -.5 as partial — they are valid negative fractions.
        return str === '-' || str === '-.' || /^-?\d+\.$/.test(str);
      },
      parseNumericInput(value) {
        const normalized = this.normalizeTypedInput(value);
        if (normalized === '' || normalized === null || normalized === undefined) {
          return null;
        }
        if (this.isPartialNumericInput(normalized)) {
          return null;
        }
        const numValue = Number(normalized);
        return Number.isFinite(numValue) ? numValue : null;
      },
      emitValue(value) {
        this.content = value;
        this.$emit('input', value);
        this.$emit('change', value);
      },
      validateMinMax(numValue) {
        if (!Number.isFinite(numValue)) {
          this.minMaxError = 'Please enter a valid number';
          return false;
        }
        if (this.min !== '' && !isNaN(Number(this.min)) && numValue < Number(this.min)) {
          this.minMaxError = `Value must be at least ${this.min}`;
          return false;
        }
        if (this.max !== '' && !isNaN(Number(this.max)) && numValue > Number(this.max)) {
          this.minMaxError = `Value must be at most ${this.max}`;
          return false;
        }
        this.minMaxError = '';
        return true;
      },
      setValue: function(){
        if (this.showMinMaxError) {
          let v = this.normalizeTypedInput(this.value);
          if (v === null || v === undefined || v === '') {
            this.tempContent = this.allowEmpty || this.allowsNegativeInput() ? '' : '0';
            this.content = this.allowEmpty || this.allowsNegativeInput() ? null : 0;
            this.minMaxError = '';
            return;
          }
          v = v.toString();
          if (v == this.ignoredFieldDefaultValue) {
            this.tempContent = "";
          } else {
            this.tempContent = v;
          }
          const numValue = this.parseNumericInput(v);
          if (numValue === null) {
            this.content = null;
            this.minMaxError = this.isPartialNumericInput(v) ? '' : 'Please enter a valid number';
            return;
          }
          this.content = numValue;
          this.validateMinMax(numValue);
        } else {
          // Legacy behavior: auto-correct values
          if (this.value === null || this.value === undefined || this.value === '') {
            if (this.allowEmpty || this.allowsNegativeInput()) {
              this.tempContent = '';
              this.content = null;
              this.minMaxError = '';
              return;
            }
          }
          const raw = this.value === null || this.value === undefined ? '' : this.value.toString();
          let v = this.checkRules(raw).toString();
          if(v == this.ignoredFieldDefaultValue){
            this.tempContent = "";
          } else {
            this.tempContent = v;
          }
          this.content = Number(v) ?? v;
        }
      },
      updateValue: function () {
        let raw = this.tempContent;

        if (raw === '' || raw === null || raw === undefined) {
          if (this.allowEmpty || this.allowsNegativeInput()) {
            this.emitValue(null);
            return;
          }
          raw = '0';
          this.tempContent = raw;
        }

        raw = this.normalizeTypedInput(raw);
        if (String(raw) !== String(this.tempContent)) {
          this.tempContent = raw;
        }

        if (this.showMinMaxError) {
          if (this.isPartialNumericInput(raw)) {
            this.minMaxError = '';
            return;
          }

          const numValue = this.parseNumericInput(raw);
          if (numValue === null) {
            this.content = null;
            this.minMaxError = 'Please enter a valid number';
            this.emitValue(null);
            return;
          }

          this.content = numValue;
          this.validateMinMax(numValue);
          this.emitValue(numValue);
        } else {
          // Legacy behavior: auto-correct values
          this.tempContent = this.checkRules(this.tempContent).toString();
          if(isNaN(Number(this.tempContent))) this.tempContent = this.checkRules(this.content).toString();
          else this.content = Number(this.tempContent) ?? this.tempContent;
          this.emitValue(this.content);
        }
      },
      setFocus: function(){
        this.$nextTick(() => {
          this.$refs.element.$el.querySelector('input').focus();
        });
      },
      checkRules: function(x){
        if(x){
          let v = Number(x)
          if(this.min && !isNaN(Number(this.min))) if(v < Number(this.min)) v = this.min;
          if(this.max && !isNaN(Number(this.max))) if(v > Number(this.max)) v = this.max;
          return v;
        }else return "";
      },
    },
    computed:{
      displayContent: {
        get() {
          return this.tempContent == 0.001 ? '' : this.tempContent;
        },
        set(value) {
          this.tempContent = value;
        }
      },
      computedErrorMessages() {
        // Show minMaxError first if it exists
        if (this.minMaxError) {
          return this.minMaxError;
        }
        if (typeof this.error === 'boolean') {
          return '';
        }
        return this.error;
      },
      computedHideDetails() {
        // Show details/error message when there's a minMaxError, even if hidedetails is true
        if (this.minMaxError) {
          return false;
        }
        return this.hidedetails;
      }
    },
    watch: {
      value: function(){
        this.setValue();
      },
    }
  }
</script>

<style>
.number-field .v-input__append-inner {
  margin-top: 0 !important;
  margin: auto !important;
}
</style>